import DraggableWidget from "./DraggableWidget";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";

interface GameEvent {
  id: string;
  minute: number;
  type: string;
  player: string;
  team: string;
  timestamp: string;
}

interface EventTimelineWidgetProps {
  id: string;
  events?: GameEvent[];
  sport?: 'soccer' | 'csgo';
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}

const EventTimelineWidget = ({ 
  id, 
  events = [], 
  sport = 'soccer', 
  onDragStart, 
  onDragEnd, 
  isDragging 
}: EventTimelineWidgetProps) => {
  const getEventIcon = (type: string, sport: string) => {
    if (sport === 'soccer') {
      switch (type) {
        case 'goal': return '⚽';
        case 'assist': return '🎯';
        case 'yellow': return '🟨';
        case 'red': return '🟥';
        case 'sub_in': return '🔄';
        case 'sub_out': return '↩️';
        default: return '📝';
      }
    } else { // csgo
      switch (type) {
        case 'kill': return '🔫';
        case 'ace': return '👑';
        case 'clutch': return '💪';
        case 'plant': return '💣';
        case 'defuse': return '🛡️';
        default: return '🎯';
      }
    }
  };

  const formatTime = (minute: number, sport: string) => {
    if (sport === 'csgo') {
      return `R${Math.ceil(minute / 2)}`; // Round number for CSGO
    }
    return `${minute}'`;
  };

  return (
    <DraggableWidget
      id={id}
      title="Event Timeline"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      isDragging={isDragging}
    >
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {events.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            <Clock className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">No events yet</p>
          </div>
        ) : (
          events.map((event) => (
            <div key={event.id} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
              <div className="text-xs font-mono text-muted-foreground min-w-[3rem]">
                {formatTime(event.minute, sport)}
              </div>
              <div className="text-lg">
                {getEventIcon(event.type, sport)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{event.player}</div>
                <div className="text-xs text-muted-foreground">{event.team}</div>
              </div>
              <Badge variant="outline" className="text-xs">
                {event.type.replace('_', ' ')}
              </Badge>
            </div>
          ))
        )}
      </div>
    </DraggableWidget>
  );
};

export default EventTimelineWidget;